import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Mic, Square, Loader2 } from "lucide-react";

interface VoiceInputProps {
  onTranscript: (text: string) => void;
}

export const VoiceInput = ({ onTranscript }: VoiceInputProps) => {
  const { toast } = useToast();
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const stopTimerRef = useRef<number | null>(null);
  const stoppingRef = useRef(false);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
        }
      });
      
      streamRef.current = stream;
      stoppingRef.current = false;
      if (stopTimerRef.current) {
        clearTimeout(stopTimerRef.current);
        stopTimerRef.current = null;
      }
      
      // Try to use the most compatible audio format
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/mp4')
        ? 'audio/mp4'
        : 'audio/webm';
      
      console.log('Using MIME type:', mimeType);
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          console.log('Audio chunk received:', e.data.size, 'bytes');
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onerror = (e) => {
        console.error('MediaRecorder error:', e);
        toast({
          title: "Recording Error",
          description: "A recording error occurred.",
          variant: "destructive",
        });
        try { mediaRecorder.stop(); } catch {}
      };
      mediaRecorder.onstop = async () => {
        console.log('Recording stopped. Total chunks:', chunksRef.current.length);
        if (stopTimerRef.current) {
          clearTimeout(stopTimerRef.current);
          stopTimerRef.current = null;
        }
        stoppingRef.current = false;
        const stream = streamRef.current;
        if (!stream) {
          console.warn('No stream ref available on stop');
        }
        if (chunksRef.current.length === 0) {
          toast({
            title: "Recording Error",
            description: "No audio data captured. Please try again.",
            variant: "destructive",
          });
          stream?.getTracks().forEach(track => track.stop());
          return;
        }
        
        const type = mediaRecorderRef.current?.mimeType || 'audio/webm';
        const audioBlob = new Blob(chunksRef.current, { type });
        console.log('Audio blob size:', audioBlob.size, 'bytes');
        
        if (audioBlob.size < 1000) { // Less than 1KB is probably too short
          toast({
            title: "Recording Too Short",
            description: "Please record a longer message.",
            variant: "destructive",
          });
          stream?.getTracks().forEach(track => track.stop());
          return;
        }
        
        await processAudio(audioBlob);
        
        // Stop all tracks
        stream?.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start(1000); // Collect data every second
      setRecording(true);
      
      toast({
        title: "Recording Started",
        description: "Speak your entry clearly...",
      });
    } catch (error) {
      console.error('Recording error:', error);
      toast({
        title: "Microphone Error",
        description: error instanceof Error ? error.message : "Could not access microphone. Please check permissions.",
        variant: "destructive",
      });
    }
  };

  const stopRecording = () => {
    console.log('Stop recording clicked', {
      hasMediaRecorder: !!mediaRecorderRef.current,
      recording,
      state: mediaRecorderRef.current?.state,
    });

    if (!mediaRecorderRef.current) {
      console.error('No media recorder found');
      setRecording(false);
      toast({
        title: "Error",
        description: "Recording stopped unexpectedly. Please try again.",
        variant: "destructive",
      });
      return;
    }

    if (stoppingRef.current) {
      console.log('Already stopping...');
      return;
    }

    stoppingRef.current = true;
    setProcessing(true);
    setRecording(false);

    try {
      if (mediaRecorderRef.current.state === 'recording') {
        try { mediaRecorderRef.current.requestData?.(); } catch {}
        console.log('Stopping media recorder...');
        mediaRecorderRef.current.stop();
      } else {
        console.log('Media recorder already inactive');
      }
    } catch (e) {
      console.error('Error attempting to stop recorder:', e);
    }

    // Fallback in case onstop doesn't fire
    if (stopTimerRef.current) {
      clearTimeout(stopTimerRef.current);
    }
    stopTimerRef.current = window.setTimeout(async () => {
      console.warn('onstop timeout reached, using fallback');
      try {
        if (chunksRef.current.length > 0) {
          const type = mediaRecorderRef.current?.mimeType || 'audio/webm';
          const blob = new Blob(chunksRef.current, { type });
          await processAudio(blob);
        } else {
          toast({
            title: "Recording Error",
            description: "No audio captured. Please try again.",
            variant: "destructive",
          });
        }
      } finally {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        stoppingRef.current = false;
        stopTimerRef.current = null;
      }
    }, 2000);
  };

  const processAudio = async (audioBlob: Blob) => {
    setProcessing(true);
    try {
      console.log('Processing audio blob...');
      
      // Convert blob to base64
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      
      await new Promise((resolve, reject) => {
        reader.onloadend = resolve;
        reader.onerror = reject;
      });

      const base64Audio = (reader.result as string).split(',')[1];
      console.log('Base64 audio length:', base64Audio.length);

      // Send to transcription function
      console.log('Calling transcribe-audio function...');
      const { data, error } = await supabase.functions.invoke('transcribe-audio', {
        body: { audio: base64Audio }
      });

      console.log('Transcription response:', { data, error });

      if (error) {
        console.error('Transcription error:', error);
        throw error;
      }

      if (data?.text) {
        console.log('Transcribed text:', data.text);
        onTranscript(data.text);
        toast({
          title: "Transcription Complete",
          description: data.text.slice(0, 50) + (data.text.length > 50 ? '...' : ''),
        });
      } else if (data?.error) {
        throw new Error(data.error);
      } else {
        throw new Error('No transcription received');
      }
    } catch (error) {
      console.error('Transcription error:', error);
      toast({
        title: "Transcription Error",
        description: error instanceof Error ? error.message : "Failed to transcribe audio. Please try again.",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center space-y-4 py-8">
      {recording ? (
        <>
          <div className="relative">
            <div className="absolute inset-0 animate-ping pointer-events-none">
              <div className="h-20 w-20 rounded-full bg-destructive/20" />
            </div>
            <Button
              size="lg"
              variant="destructive"
              onClick={stopRecording}
              className="h-20 w-20 rounded-full"
            >
              <Square className="h-8 w-8" />
            </Button>
          </div>
          <div className="mt-4">
            <Button
              onClick={stopRecording}
              variant="secondary"
              size="sm"
              type="button"
              aria-label="Stop recording (alternate)"
            >
              <Square className="h-4 w-4 mr-2" />
              Stop Recording
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">Recording... Click to stop</p>
        </>
      ) : processing ? (
        <>
          <div className="h-20 w-20 flex items-center justify-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
          </div>
          <p className="text-sm text-muted-foreground">Transcribing audio...</p>
        </>
      ) : (
        <>
          <Button
            size="lg"
            onClick={startRecording}
            className="h-20 w-20 rounded-full"
          >
            <Mic className="h-8 w-8" />
          </Button>
          <div className="text-center">
            <p className="text-sm font-medium">Tap to start recording</p>
            <p className="text-xs text-muted-foreground mt-1">
              Speak your entry clearly
            </p>
          </div>
        </>
      )}

      <div className="text-xs text-muted-foreground text-center max-w-xs">
        <strong>Try saying:</strong>
        <ul className="mt-2 space-y-1">
          <li>"Create invoice for Tech Corp 50000 rupees"</li>
          <li>"Add task to follow up with client tomorrow"</li>
          <li>"Log expense for lunch 600 rupees"</li>
        </ul>
      </div>
    </div>
  );
};